/**
 * DeliveryStopCard - Individual delivery stop card
 * Used in the delivery run view for each order
 *
 * Supports three modes:
 * - Active sale stop: Navigate, Defer, Delivered buttons
 * - Custom stop: Navigate, Remove buttons (no DB record)
 * - Deferred stop: Restore button only
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Check, MapPin, Package, SkipForward, Undo2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export function DeliveryStopCard({
  stop,
  index,
  isDelivered,
  isDeferred,
  isCustomStop,
  onMarkDelivered,
  onNavigate,
  onDefer,
  onUndefer,
  onRemoveCustomStop,
  isDragging
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: stop.id, disabled: isDeferred })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  // Format items summary
  const hasItems = stop.items?.length > 0
  const itemsSummary = hasItems
    ? stop.items.map(item => `${item.quantity}× ${item.productName}`).join(', ')
    : null

  // Truncate if too long
  const truncatedSummary = itemsSummary && itemsSummary.length > 50
    ? itemsSummary.substring(0, 47) + '...'
    : itemsSummary

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'bg-white rounded-lg border shadow-sm mb-3 overflow-hidden',
        'transition-all duration-200',
        isDelivered && 'bg-gray-50 opacity-75',
        isDeferred && 'bg-amber-50/50 opacity-75',
        isDragging && 'shadow-lg ring-2 ring-blue-500'
      )}
    >
      {/* Card Header with drag handle */}
      <div className="flex items-stretch">
        {/* Drag Handle */}
        {!isDeferred && (
          <div
            {...attributes}
            {...listeners}
            className={cn(
              'flex items-center justify-center w-10 bg-gray-50 cursor-grab active:cursor-grabbing',
              'touch-none select-none',
              isDelivered && 'cursor-default'
            )}
          >
            <GripVertical className="w-5 h-5 text-gray-400" />
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 p-4">
          {/* Stop Number + Customer Name */}
          <div className="flex items-center gap-2 mb-1">
            <span className={cn(
              'flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium',
              isDelivered
                ? 'bg-green-100 text-green-700'
                : isDeferred
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-700'
            )}>
              {isDelivered ? <Check className="w-3.5 h-3.5" /> : isDeferred ? <SkipForward className="w-3.5 h-3.5" /> : index + 1}
            </span>
            <span className={cn(
              'font-medium text-gray-900',
              isDelivered && 'line-through text-gray-500',
              isDeferred && 'text-gray-500'
            )}>
              {stop.customerName}
            </span>
            {isCustomStop && (
              <Badge className="border-purple-300 bg-purple-50 text-purple-700 text-[10px] px-1.5 py-0">
                Custom
              </Badge>
            )}
            {!isCustomStop && stop.branchName && stop.branchName !== 'Main Company' && (
              <span className="text-sm text-gray-500">
                ({stop.branchName})
              </span>
            )}
          </div>

          {/* Address */}
          <div className="flex items-start gap-1.5 text-sm text-gray-600 mb-2">
            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
            <span className={cn((isDelivered || isDeferred) && 'text-gray-400')}>
              {stop.address || 'No address'}
            </span>
          </div>

          {/* Items - hidden when empty (e.g. custom stops) */}
          {hasItems && (
            <div className="flex items-start gap-1.5 text-sm text-gray-500">
              <Package className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <span className={cn((isDelivered || isDeferred) && 'text-gray-400')}>
                {truncatedSummary}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons - Active sale stop */}
      {!isDelivered && !isDeferred && !isCustomStop && (
        <div className="flex border-t">
          <Button
            variant="ghost"
            className="flex-1 h-12 rounded-none text-blue-600 hover:bg-blue-50 hover:text-blue-700"
            onClick={() => onNavigate(stop)}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Navigate
          </Button>
          <div className="w-px bg-border" />
          <Button
            variant="ghost"
            className="flex-1 h-12 rounded-none text-amber-600 hover:bg-amber-50 hover:text-amber-700"
            onClick={() => onDefer?.(stop.id)}
          >
            <SkipForward className="w-4 h-4 mr-2" />
            Defer
          </Button>
          <div className="w-px bg-border" />
          <Button
            variant="ghost"
            className="flex-1 h-12 rounded-none text-green-600 hover:bg-green-50 hover:text-green-700"
            onClick={() => onMarkDelivered(stop.id)}
          >
            <Check className="w-4 h-4 mr-2" />
            Delivered
          </Button>
        </div>
      )}

      {/* Action Buttons - Custom stop */}
      {!isDelivered && !isDeferred && isCustomStop && (
        <div className="flex border-t">
          <Button
            variant="ghost"
            className="flex-1 h-12 rounded-none text-blue-600 hover:bg-blue-50 hover:text-blue-700"
            onClick={() => onNavigate(stop)}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Navigate
          </Button>
          <div className="w-px bg-border" />
          <Button
            variant="ghost"
            className="flex-1 h-12 rounded-none text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => onRemoveCustomStop?.(stop.id)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Remove
          </Button>
        </div>
      )}

      {/* Action Buttons - Deferred stop */}
      {isDeferred && (
        <div className="flex border-t">
          <Button
            variant="ghost"
            className="w-full h-12 rounded-none text-amber-600 hover:bg-amber-50 hover:text-amber-700"
            onClick={() => onUndefer?.(stop.id)}
          >
            <Undo2 className="w-4 h-4 mr-2" />
            Restore
          </Button>
        </div>
      )}

      {/* Delivered indicator */}
      {isDelivered && (
        <div className="flex items-center justify-center h-12 border-t bg-green-50 text-green-700 text-sm font-medium">
          <Check className="w-4 h-4 mr-2" />
          Delivered
        </div>
      )}
    </div>
  )
}

export default DeliveryStopCard
